import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Users, AlertTriangle, Repeat, GripVertical } from 'lucide-react';
import {
  xamplinApi,
  entityIdOf,
  type XpStructureEvent,
  type XpStructureReporter,
} from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

/**
 * Staffing & roster (build-v2 F5). Drag a reporter from the roster onto an
 * event to assign them (≤4/event). Flags scheduling conflicts (same reporter on
 * two time-overlapping events) and surfaces same-day rollover opportunities
 * (a finishing team near an understaffed gap). Roster is derived from the
 * project's existing reporters — no separate endpoint needed.
 */
interface FlatEvent {
  id: number;
  name: string;
  group: string;
  start: number;
  end: number;
  lat: number | null;
  lng: number | null;
  reporters: XpStructureReporter[];
}
interface RosterMember {
  email: string;
  name: string;
  reporterType?: string | null;
}

function toRad(d: number) {
  return (d * Math.PI) / 180;
}
function haversineMi(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 3958.8;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
const overlaps = (a: FlatEvent, b: FlatEvent) => a.start < b.end && b.start < a.end;
const sameDay = (a: number, b: number) =>
  new Date(a).toISOString().slice(0, 10) === new Date(b).toISOString().slice(0, 10);

export function StaffingPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? Number(id) : 0;
  const qc = useQueryClient();
  const [dragMember, setDragMember] = useState<RosterMember | null>(null);

  const q = useQuery({
    queryKey: queryKeys.xamplin.projectStructure(projectId),
    queryFn: () => xamplinApi.projects.structure(projectId),
    enabled: projectId > 0,
  });

  const events = useMemo<FlatEvent[]>(() => {
    const groups = q.data?.structured?.project?.eventGroups ?? [];
    const flat: FlatEvent[] = [];
    for (const g of groups) {
      for (const e of (g.events ?? []) as XpStructureEvent[]) {
        const start = e.startDate ? Date.parse(e.startDate) : NaN;
        const end = e.endDate ? Date.parse(e.endDate) : start;
        if (!Number.isFinite(start)) continue;
        flat.push({
          id: entityIdOf(e.id),
          name: e.name ?? '—',
          group: g.name ?? '',
          start,
          end: Number.isFinite(end) ? end : start + 3 * 3600_000,
          lat: e.lat ?? null,
          lng: e.lng ?? null,
          reporters: e.reporters ?? [],
        });
      }
    }
    return flat.sort((a, b) => a.start - b.start);
  }, [q.data]);

  // Roster = distinct reporters across the project, by email.
  const roster = useMemo<RosterMember[]>(() => {
    const byEmail = new Map<string, RosterMember>();
    for (const e of events)
      for (const r of e.reporters) {
        const email = r.email ?? r.name ?? '';
        if (!email || byEmail.has(email)) continue;
        byEmail.set(email, { email, name: r.name ?? email, reporterType: r.reporterType });
      }
    return [...byEmail.values()];
  }, [events]);

  // Conflicts: same reporter (by email) on two time-overlapping events.
  const conflicts = useMemo(() => {
    const byEmail = new Map<string, FlatEvent[]>();
    for (const e of events)
      for (const r of e.reporters) {
        const k = r.email ?? r.name ?? '';
        if (!k) continue;
        const list = byEmail.get(k) ?? [];
        list.push(e);
        byEmail.set(k, list);
      }
    const out: { email: string; a: FlatEvent; b: FlatEvent }[] = [];
    for (const [email, list] of byEmail) {
      for (let i = 0; i < list.length; i++)
        for (let j = i + 1; j < list.length; j++)
          if (overlaps(list[i], list[j])) out.push({ email, a: list[i], b: list[j] });
    }
    return out;
  }, [events]);
  const conflictEventIds = useMemo(() => {
    const s = new Set<number>();
    conflicts.forEach((c) => {
      s.add(c.a.id);
      s.add(c.b.id);
    });
    return s;
  }, [conflicts]);

  // Rollover: a staffed event that finishes before a nearby same-day
  // understaffed event starts (≤15 mi). Surfaces redeployment opportunities.
  const rollovers = useMemo(() => {
    const out: { donor: FlatEvent; gap: FlatEvent; mi: number }[] = [];
    for (const donor of events) {
      if (donor.reporters.length === 0 || donor.lat == null) continue;
      for (const gap of events) {
        if (gap.id === donor.id || gap.reporters.length > 0 || gap.lat == null) continue;
        if (!sameDay(donor.start, gap.start)) continue;
        if (donor.end > gap.start) continue; // donor must finish first
        const mi = haversineMi(donor.lat, donor.lng!, gap.lat, gap.lng!);
        if (mi <= 15) out.push({ donor, gap, mi });
      }
    }
    return out.slice(0, 8);
  }, [events]);

  const assign = useMutation({
    mutationFn: (vars: { event: number; member: RosterMember }) => {
      const [firstname, ...rest] = vars.member.name.split(' ');
      return xamplinApi.reporters.assign({
        event: vars.event,
        email: vars.member.email,
        firstname: firstname || vars.member.email,
        lastname: rest.join(' ') || '',
      });
    },
    onSuccess: () => {
      toast({ tone: 'success', title: 'Reporter asignado' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.projectStructure(projectId) });
    },
    onError: (e) => toast({ tone: 'error', title: (e as Error).message || 'No se pudo asignar' }),
  });

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link
          to={`/projects/${projectId}`}
          className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Staffing & roster
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            {roster.length} reporters · arrastra para asignar
          </p>
        </div>
      </div>

      {q.isLoading && <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando…</div>}

      {/* Conflicts + rollover insights */}
      {(conflicts.length > 0 || rollovers.length > 0) && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {conflicts.length > 0 && (
            <div className="rounded-xl border border-[#f4b740]/40 bg-[#f4b740]/10 p-3">
              <div className="mb-1.5 flex items-center gap-2 text-sm font-bold text-[#f4b740]">
                <AlertTriangle className="h-4 w-4" /> {conflicts.length} conflicto(s) de horario
              </div>
              <ul className="flex flex-col gap-1 text-[11px] text-[var(--color-xk-text-muted)]">
                {conflicts.slice(0, 4).map((c, i) => (
                  <li key={i}>
                    <span className="text-[var(--color-xk-lavender)]">{c.email}</span>: «{c.a.name}» ⨯ «{c.b.name}»
                  </li>
                ))}
              </ul>
            </div>
          )}
          {rollovers.length > 0 && (
            <div className="rounded-xl border border-[#4dd28b]/40 bg-[#4dd28b]/10 p-3">
              <div className="mb-1.5 flex items-center gap-2 text-sm font-bold text-[#4dd28b]">
                <Repeat className="h-4 w-4" /> {rollovers.length} rollover posible(s)
              </div>
              <ul className="flex flex-col gap-1 text-[11px] text-[var(--color-xk-text-muted)]">
                {rollovers.slice(0, 4).map((r, i) => (
                  <li key={i}>
                    «{r.donor.name}» → «{r.gap.name}» · {r.mi.toFixed(1)} mi
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        {/* Roster */}
        <aside className="flex flex-col gap-2 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-3">
          <h2 className="mono flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            <Users className="h-3.5 w-3.5" /> Roster
          </h2>
          {roster.length === 0 && (
            <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">Sin reporters aún.</span>
          )}
          {roster.map((m) => (
            <div
              key={m.email}
              draggable
              onDragStart={() => setDragMember(m)}
              onDragEnd={() => setDragMember(null)}
              className="flex cursor-grab items-center gap-1.5 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-2 py-1.5 text-xs text-[var(--color-xk-lavender)] active:cursor-grabbing"
            >
              <GripVertical className="h-3.5 w-3.5 flex-none text-[var(--color-xk-text-subtle)]" />
              <span className="min-w-0 flex-1 truncate">{m.name}</span>
              {m.reporterType && (
                <span className="mono text-[9px] text-[var(--color-xk-text-subtle)]">{m.reporterType}</span>
              )}
            </div>
          ))}
        </aside>

        {/* Events as drop targets */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {events.map((e) => {
            const full = e.reporters.length >= 4;
            const conflict = conflictEventIds.has(e.id);
            return (
              <div
                key={e.id}
                onDragOver={(ev) => {
                  if (!full) ev.preventDefault();
                }}
                onDrop={() => {
                  if (full || !dragMember) return;
                  if (e.reporters.some((r) => (r.email ?? r.name) === dragMember.email)) {
                    toast({ tone: 'info', title: 'Ya está asignado a este evento' });
                    return;
                  }
                  assign.mutate({ event: e.id, member: dragMember });
                  setDragMember(null);
                }}
                className={cn(
                  'flex flex-col gap-2 rounded-xl border bg-[var(--color-xk-surface)] p-3 transition-colors',
                  conflict
                    ? 'border-[#f4b740]/50'
                    : dragMember && !full
                      ? 'border-dashed border-[var(--color-xk-accent)]'
                      : 'border-[var(--color-xk-border)]',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-[var(--color-xk-lavender)]">{e.name}</div>
                    <div className="mono text-[10px] text-[var(--color-xk-text-subtle)]">
                      {new Date(e.start).toLocaleDateString('es', { day: 'numeric', month: 'short', timeZone: 'UTC' })} ·{' '}
                      {new Date(e.start).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                    </div>
                  </div>
                  {conflict && <AlertTriangle className="h-4 w-4 flex-none text-[#f4b740]" />}
                </div>
                <div className="flex flex-wrap gap-1">
                  {e.reporters.map((r) => (
                    <span
                      key={r.id}
                      className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]"
                    >
                      👤 {r.name || r.email}
                    </span>
                  ))}
                  <span
                    className={cn(
                      'mono rounded-full px-2 py-0.5 text-[10px]',
                      full ? 'text-[var(--color-xk-text-subtle)]' : 'text-[var(--color-xk-accent)]',
                    )}
                  >
                    {full ? 'lleno (4)' : `${e.reporters.length}/4 · suelta aquí`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
